'use client';

// TEMPORARY diagnostic instrumentation for the "whole team-details modal
// disappears for a split second, with no user interaction" flicker.
// Render-level capture (every commit) because the existing effect-on-debugState
// logging coalesces sub-second transients. Filter in Seq by instanceId below.
// Remove this whole file + its call sites once the cause is confirmed + fixed.

import { useEffect, useId, useLayoutEffect, useRef } from 'react';
import { mediaQueries } from '@/theme/breakpoints';
import {
  usePersistentDebugLogger,
  type PersistentDebugLogger,
} from './use-persistent-debug-logger';

export const WHOLE_MODAL_FLICKER_INSTANCE_ID =
  'team-details-whole-modal-flicker--2026-06-16';
export const WHOLE_MODAL_FLICKER_FEATURE = 'team-details-whole-modal-flicker';

// Stamped once when THIS module evaluates. If it changes between events, the
// module was reloaded (HMR / Fast Refresh / chunk reload) — i.e. a dev remount.
export const MODULE_EVAL_ID = Date.now().toString(36);
const FLICKER_MOUNT_INSTANCE_SEPARATOR = '#';
const FLICKER_PORTAL_ROOT_ID_PREFIX_PATTERN = /^portal-root/;
const FLICKER_PREPAGE_ROOT_ID = 'prepage';
const FLICKER_PORTAL_ROOT_ID = 'portal-root';

export enum FlickerProbeEvent {
  Render = 'flicker-render',
  Mount = 'flicker-mount',
  Unmount = 'flicker-unmount',
  ExitComplete = 'flicker-exit-complete',
  AnimationStart = 'flicker-animation-start',
  PortalContainer = 'flicker-portal-container',
  CoverSample = 'flicker-cover-sample',
  DomMutation = 'flicker-dom-mutation',
}

export interface ViewportMatchMediaSnapshot {
  isPortrait: boolean;
  isWeb: boolean;
  isCompact: boolean;
  isMobile: boolean;
  allFalse: boolean;
  innerWidth: number;
  innerHeight: number;
  devicePixelRatio: number;
  visibilityState: string;
  documentHidden: boolean;
}

function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : 0;
}

// Stable per-mount id. A remount (new component instance) gets a new id, so two
// consecutive render events with different mountInstanceId == a remount, not a
// re-render.
export function useMountInstanceId(): string {
  const reactInstanceId = useId();
  return `${MODULE_EVAL_ID}${FLICKER_MOUNT_INSTANCE_SEPARATOR}${reactInstanceId}`;
}

// Reads the four media queries useViewport() composes, live at log time, so a
// transient viewport=null (all four false) is explained by which query flipped.
export function captureViewportMatchMedia(): ViewportMatchMediaSnapshot | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const isPortrait = window.matchMedia('(orientation: portrait)').matches;
  const isWeb = window.matchMedia(mediaQueries.web).matches;
  const isCompact = window.matchMedia(mediaQueries.compact).matches;
  const isMobile = window.matchMedia(mediaQueries.mobile).matches;

  return {
    isPortrait,
    isWeb,
    isCompact,
    isMobile,
    allFalse: !isPortrait && !isWeb && !isCompact && !isMobile,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
    visibilityState: document.visibilityState,
    documentHidden: document.hidden,
  };
}

export function useFlickerProbeLogger(
  component: string,
  metadata?: Record<string, unknown>,
): PersistentDebugLogger {
  return usePersistentDebugLogger({
    component,
    instanceId: WHOLE_MODAL_FLICKER_INSTANCE_ID,
    metadata: {
      feature: WHOLE_MODAL_FLICKER_FEATURE,
      moduleEvalId: MODULE_EVAL_ID,
      ...(metadata ?? {}),
    },
  });
}

// Logs once per commit ONLY when the serialized snapshot changed since the last
// commit — so every transition (incl. a false→true that flips back) is captured
// without flooding Seq on no-op re-renders.
export function useRenderChangeProbe(input: {
  logger: PersistentDebugLogger;
  eventName: FlickerProbeEvent;
  snapshot: Record<string, unknown>;
}): void {
  const mountInstanceId = useMountInstanceId();
  const lastSerializedRef = useRef<string | null>(null);
  const renderCountRef = useRef(0);

  useLayoutEffect(() => {
    renderCountRef.current += 1;
    const serialized = JSON.stringify(input.snapshot);
    if (lastSerializedRef.current === serialized) {
      return;
    }

    const previousSerialized = lastSerializedRef.current;
    lastSerializedRef.current = serialized;
    input.logger.logStateTransition({
      name: input.eventName,
      from: previousSerialized ? JSON.parse(previousSerialized) : null,
      to: input.snapshot,
      payload: {
        mountInstanceId,
        renderCount: renderCountRef.current,
        tMs: nowMs(),
        first: previousSerialized === null,
      },
    });
  });
}

// Mount / unmount markers — distinguishes a hard remount (unmount→mount with no
// AnimatePresence exit) from a state-driven exit animation. mountInstanceId ties
// an unmount to its later remount.
export function useMountProbe(input: {
  logger: PersistentDebugLogger;
  label: string;
}): void {
  const { logger, label } = input;
  const mountInstanceId = useMountInstanceId();
  useLayoutEffect(() => {
    logger.logMarker({
      name: FlickerProbeEvent.Mount,
      payload: { label, mountInstanceId, tMs: nowMs() },
    });
    return () => {
      logger.logMarker({
        name: FlickerProbeEvent.Unmount,
        payload: { label, mountInstanceId, tMs: nowMs() },
      });
    };
  }, [logger, label, mountInstanceId]);
}

interface PaintElementSummary {
  tag: string;
  id: string | null;
  dataName: string | null;
  zIndex: string;
  opacity: string;
  visibility: string;
  w: number;
  h: number;
}

interface CoverSampleState {
  lastSampleMs: number;
  lastFingerprint: string;
}

interface CoverSampleSnapshot {
  fingerprint: string;
  portalConnected: boolean;
  portalRootExists: boolean;
  stack: PaintElementSummary[];
  tMs: number;
}

interface MaybeLogCoverSampleInput {
  logger: PersistentDebugLogger;
  sampleState: CoverSampleState;
  snapshot: CoverSampleSnapshot;
}

const FLICKER_COVER_SAMPLE_INTERVAL_MS = 50;
const FLICKER_COVER_SAMPLE_STACK_LIMIT = 5;

function summarizePaintElement(element: Element): PaintElementSummary {
  const htmlElement = element as HTMLElement;
  const style = window.getComputedStyle(htmlElement);
  const rect = htmlElement.getBoundingClientRect();
  return {
    tag: htmlElement.tagName,
    id: htmlElement.id || null,
    dataName: htmlElement.dataset?.name ?? null,
    zIndex: style.zIndex,
    opacity: style.opacity,
    visibility: style.visibility,
    w: Math.round(rect.width),
    h: Math.round(rect.height),
  };
}

function captureCoverSampleSnapshot(): CoverSampleSnapshot {
  const tMs = nowMs();
  const centerX = Math.floor(window.innerWidth / 2);
  const centerY = Math.floor(window.innerHeight / 2);
  const stack = document
    .elementsFromPoint(centerX, centerY)
    .slice(0, FLICKER_COVER_SAMPLE_STACK_LIMIT)
    .map(summarizePaintElement);
  const portalRoot = document.getElementById(FLICKER_PORTAL_ROOT_ID);
  const portalConnected = portalRoot?.isConnected ?? false;
  const portalRootExists = Boolean(portalRoot);

  return {
    fingerprint: JSON.stringify({
      topTag: stack[0]?.tag ?? null,
      topId: stack[0]?.id ?? null,
      topDataName: stack[0]?.dataName ?? null,
      portalConnected,
    }),
    portalConnected,
    portalRootExists,
    stack,
    tMs,
  };
}

function maybeLogCoverSample({
  logger,
  sampleState,
  snapshot,
}: MaybeLogCoverSampleInput): void {
  if (snapshot.fingerprint === sampleState.lastFingerprint) {
    return;
  }

  const previous = sampleState.lastFingerprint;
  sampleState.lastFingerprint = snapshot.fingerprint;
  logger.logMarker({
    name: FlickerProbeEvent.CoverSample,
    payload: {
      tMs: snapshot.tMs,
      stack: snapshot.stack,
      portalRootExists: snapshot.portalRootExists,
      portalConnected: snapshot.portalConnected,
      changedFrom: previous || null,
    },
  });
}

// Catches the "state stays open but nothing paints / something covers it" class:
// portal-root detached, modal element gone, OR a higher-z overlay (Cprepage,
// message modal, stale popover portal) painted over the modal center. Samples at
// ~20Hz while active and logs ONLY when the paint fingerprint changes, plus a
// body MutationObserver for portal-root / prepage add/remove.
export function useFlickerDomWatch(input: {
  logger: PersistentDebugLogger;
  active: boolean;
}): void {
  const { logger, active } = input;
  useEffect(() => {
    if (!active || typeof window === 'undefined') {
      return undefined;
    }

    let rafId = 0;
    const sampleState: CoverSampleState = {
      lastSampleMs: 0,
      lastFingerprint: '',
    };

    const sample = (): void => {
      const t = nowMs();
      if (t - sampleState.lastSampleMs >= FLICKER_COVER_SAMPLE_INTERVAL_MS) {
        sampleState.lastSampleMs = t;
        maybeLogCoverSample({
          logger,
          sampleState,
          snapshot: captureCoverSampleSnapshot(),
        });
      }
      rafId = window.requestAnimationFrame(sample);
    };
    rafId = window.requestAnimationFrame(sample);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const isInteresting = (node: Node): boolean =>
          node instanceof HTMLElement &&
          (FLICKER_PORTAL_ROOT_ID_PREFIX_PATTERN.test(node.id) ||
            node.id === FLICKER_PREPAGE_ROOT_ID ||
            node.id === FLICKER_PORTAL_ROOT_ID);
        const added = Array.from(mutation.addedNodes).filter(isInteresting);
        const removed = Array.from(mutation.removedNodes).filter(isInteresting);
        if (added.length === 0 && removed.length === 0) {
          return;
        }
        logger.logMarker({
          name: FlickerProbeEvent.DomMutation,
          payload: {
            tMs: nowMs(),
            added: added.map((node) => (node as HTMLElement).id),
            removed: removed.map((node) => (node as HTMLElement).id),
            bodyChildCount: document.body.childElementCount,
          },
        });
      });
    });
    observer.observe(document.body, { childList: true });

    return () => {
      window.cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [active, logger]);
}
